// ---------------------------------------------------------------------------
// UITLAdminWrapper — Wraps the UITL Admin SDK components for Quantato
//
// Reads from Quantato's AuthContext to get the current user, maps them to
// the SDK user format, and renders the suite of UITL widgets.
// Only renders if the UITL API key is configured.
// ---------------------------------------------------------------------------

import React, { useMemo } from 'react';
import {
  UITLProvider,
  ChatWidget,
  FeedbackButton as UITLFeedbackButton,
  AnnouncementBanner,
} from '@uitl/admin-sdk';
import type { UITLConfig } from '@uitl/admin-sdk';
import { useAuth } from '../contexts/AuthContext';

const UITL_API_KEY = process.env.REACT_APP_UITL_API_KEY || '';

export default function UITLAdminWrapper() {
  const { user } = useAuth();

  // Build a stable config object that updates when the user changes
  const config = useMemo<UITLConfig>(() => {
    const cfg: UITLConfig = {
      apiKey: UITL_API_KEY,
      features: ['chat', 'feedback', 'announcements'],
      user: user
        ? {
            id: String(user.id),
            email: user.email,
            role: user.is_admin ? 'admin' : 'member',
            displayName: user.display_name,
          }
        : undefined,
    };
    return cfg;
  }, [user]);

  // Don't render anything if no API key is configured
  if (!UITL_API_KEY) {
    return null;
  }

  return (
    <UITLProvider config={config}>
      <ChatWidget />
      <UITLFeedbackButton />
      <AnnouncementBanner />
    </UITLProvider>
  );
}
